import axios from "axios";
import * as Sentry from "@sentry/react";
import { accessToken, silentRefresh, isSessionDead } from "../auth/token.js";
import { getApiBaseUrl } from "../utils/apiBase.js";
import { shouldReportToSentry } from "../utils/shouldReportToSentry.js";

const API = getApiBaseUrl().replace(/\/+$/, "");

if (!import.meta.env.VITE_API_URL) {
  console.warn(
    "[axios.js] VITE_API_URL nicht gesetzt – verwende relatives '/api' (Vite-Proxy).",
  );
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const AUTH_URL = /\/auth\/(login|refresh|logout)$/;

api.interceptors.request.use((config) => {
  // Nach gescheitertem Refresh keine Requests mehr bis zum nächsten Login
  if (isSessionDead() && !/\/auth\/login$/.test(config.url || "")) {
    return Promise.reject(new axios.CanceledError("Session beendet", config));
  }

  let token = accessToken;

  if (!token && typeof window !== "undefined") {
    try {
      const fromStorage = window.localStorage
        ? window.localStorage.getItem("token")
        : null;
      if (
        fromStorage &&
        typeof fromStorage === "string" &&
        fromStorage.trim().length > 0
      ) {
        token = fromStorage.trim();
      }
    } catch {
      // ignore localStorage access errors (e.g. private mode)
    }
  }

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = "Bearer " + token;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    if (shouldReportToSentry(error)) {
      Sentry.captureException(error, {
        extra: {
          url: original.url,
          method: original.method,
          status: error.response?.status,
        },
      });
    }
    if (
      error.response &&
      error.response.status === 401 &&
      !original._retry &&
      !AUTH_URL.test(original.url || "")
    ) {
      const newToken = await silentRefresh();
      if (!newToken) {
        console.warn(
          "[axios] Token refresh fehlgeschlagen, Benutzer sollte ausgeloggt werden",
        );
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("auth:logout-required"));
        }
        return Promise.reject(error);
      }
      original._retry = true;
      original.headers = original.headers || {};
      original.headers.Authorization = "Bearer " + newToken;
      return api(original);
    }
    return Promise.reject(error);
  },
);
